<template>
    <Head :title="$t('auth.login.title')" />

    <div
        class="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-8"
    >
        <div class="w-full max-w-md">
            <div class="flex justify-center mb-8">
                <a href="/">
                    <ApplicationLogo class="h-12 w-auto" />
                </a>
            </div>

            <div class="bg-white rounded-2xl shadow-lg overflow-hidden">
                <!-- Progress bar (steps 1 and 2 only) -->
                <div v-if="step < 3" class="flex">
                    <div
                        class="h-1 flex-1 transition-all duration-300"
                        :class="step >= 1 ? 'bg-primary-500' : 'bg-gray-200'"
                    />
                    <div
                        class="h-1 flex-1 transition-all duration-300"
                        :class="step >= 2 ? 'bg-primary-500' : 'bg-gray-200'"
                    />
                </div>

                <div class="px-8 pt-8 pb-10">
                    <Transition
                        enter-active-class="transition-all duration-300 ease-out"
                        enter-from-class="opacity-0 translate-x-4"
                        enter-to-class="opacity-100 translate-x-0"
                        leave-active-class="transition-all duration-200 ease-in"
                        leave-from-class="opacity-100 translate-x-0"
                        leave-to-class="opacity-0 -translate-x-4"
                        mode="out-in"
                        @after-enter="onStepEntered"
                    >
                        <!-- Step 1: Email -->
                        <div v-if="step === 1" key="step1">
                            <p
                                class="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-2"
                                >{{ $t('common.step_one') }}</p
                            >
                            <h1 class="text-2xl font-bold text-gray-900">{{ $t('auth.login.welcome_back') }}</h1>
                            <p class="mt-2 text-gray-500">{{ $t('auth.email_prompt') }}</p>

                            <form class="mt-8 space-y-5" @submit.prevent="checkEmail">
                                <div>
                                    <InputLabel for="email" :value="$t('auth.email_label')" />
                                    <TextInput
                                        id="email"
                                        ref="emailInput"
                                        v-model="form.email"
                                        type="email"
                                        class="mt-1 block w-full"
                                        autocomplete="email"
                                        required
                                    />
                                    <InputError class="mt-1.5" :message="emailError" />
                                </div>

                                <button
                                    type="submit"
                                    class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-500 active:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
                                    :disabled="processing || !form.email.trim()"
                                    :class="{ 'opacity-50 cursor-not-allowed': processing }"
                                >
                                    <svg
                                        v-if="processing"
                                        class="h-4 w-4 animate-spin"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            class="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            stroke-width="4"
                                        />
                                        <path
                                            class="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        />
                                    </svg>
                                    <template v-else>
                                        {{ $t('common.continue') }}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            stroke-width="2.5"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                                            />
                                        </svg>
                                    </template>
                                </button>
                            </form>
                        </div>

                        <!-- Step 2: Password -->
                        <div v-else-if="step === 2" key="step2">
                            <p
                                class="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-2"
                                >{{ $t('common.step_two') }}</p
                            >
                            <h1 class="text-2xl font-bold text-gray-900">{{ $t('auth.login.password_prompt') }}</h1>
                            <p class="mt-2 text-gray-500 truncate"
                                >{{ $t('auth.login.logging_in_as') }}
                                <span class="font-medium text-gray-700">{{ form.email }}</span></p
                            >

                            <form class="mt-8 space-y-5" @submit.prevent="submit">
                                <div>
                                    <div class="flex items-center justify-between">
                                        <InputLabel for="password" :value="$t('auth.password_label')" />
                                        <Link
                                            :href="route('password.request')"
                                            class="text-xs text-primary-600 hover:text-primary-500 underline focus:outline-none"
                                        >
                                            {{ $t('auth.login.forgot_password') }}
                                        </Link>
                                    </div>
                                    <TextInput
                                        id="password"
                                        ref="passwordInput"
                                        v-model="form.password"
                                        type="password"
                                        class="mt-1 block w-full"
                                        autocomplete="current-password"
                                        required
                                    />
                                    <InputError class="mt-1.5" :message="errors.email" />
                                </div>

                                <div>
                                    <label
                                        class="flex items-center gap-2.5 cursor-pointer select-none"
                                    >
                                        <input
                                            v-model="form.remember"
                                            type="checkbox"
                                            class="rounded border-gray-300 text-primary-500 shadow-sm focus:ring-primary-400"
                                        />
                                        <span class="text-sm text-gray-600">{{ $t('auth.login.remember_me') }}</span>
                                    </label>
                                </div>

                                <div class="flex items-center gap-3 pt-1">
                                    <button
                                        type="button"
                                        class="flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                                        @click="goBack"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            stroke-width="2.5"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                                            />
                                        </svg>
                                        {{ $t('common.back') }}
                                    </button>

                                    <button
                                        type="submit"
                                        class="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-500 active:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                        :class="{ 'opacity-50 cursor-not-allowed': processing }"
                                        :disabled="processing"
                                    >
                                        <svg
                                            v-if="processing"
                                            class="h-4 w-4 animate-spin"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                class="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                stroke-width="4"
                                            />
                                            <path
                                                class="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            />
                                        </svg>
                                        {{ $t('auth.login.submit') }}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <!-- Step 3: Link sent -->
                        <div v-else key="step3">
                            <div
                                class="flex items-center justify-center w-12 h-12 rounded-full bg-primary-50 mb-5"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="w-6 h-6 text-primary-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="1.75"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                                    />
                                </svg>
                            </div>

                            <h1 class="text-2xl font-bold text-gray-900">{{ $t('auth.login.check_inbox') }}</h1>
                            <p class="mt-2 text-gray-500">
                                {{ $t('auth.login.link_sent_before') }}
                                <span class="font-medium text-gray-700">{{ form.email }}</span
                                >{{ $t('auth.login.link_sent_after') }}
                            </p>

                            <p class="mt-4 text-sm text-gray-400"
                                >{{ $t('auth.login.no_email') }}</p
                            >

                            <div class="mt-4 flex gap-2.5 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="mt-0.5 h-4 w-4 shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <span>
                                    {{ $t('auth.login.unknown_email_info') }}
                                </span>
                            </div>

                            <button
                                type="button"
                                class="mt-6 flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 focus:outline-none"
                                @click="goBack"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="2.5"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                                    />
                                </svg>
                                {{ $t('auth.login.try_another_email') }}
                            </button>
                        </div>
                    </Transition>

                    <div class="mt-6 text-center text-sm text-gray-500">
                        {{ $t('auth.login.no_account') }}
                        <Link
                            :href="route('register')"
                            class="text-primary-600 font-medium underline hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                        >
                            {{ $t('auth.register_link') }}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import ApplicationLogo from '@/Components/ApplicationLogo.vue';
    import InputError from '@/Components/InputError.vue';
    import InputLabel from '@/Components/InputLabel.vue';
    import TextInput from '@/Components/TextInput.vue';
    import { Head, Link, router } from '@inertiajs/vue3';

    export default {
        data() {
            return {
                // step 1 = email, step 2 = password, step 3 = link sent
                step: 1,
                processing: false,
                emailError: null,
                form: {
                    email: '',
                    password: '',
                    remember: false,
                },
            };
        },

        computed: {
            errors() {
                return this.$page.props.errors;
            },
        },

        mounted() {
            this.$nextTick(() => {
                if (this.$refs.emailInput) {
                    this.$refs.emailInput.focus();
                }
            });
        },

        methods: {
            async checkEmail() {
                if (!this.form.email.trim()) {
                    return;
                }

                this.processing = true;
                this.emailError = null;

                try {
                    const { data } = await axios.post(route('login.check-email'), {
                        email: this.form.email,
                    });

                    this.step = data.has_password ? 2 : 3;
                } catch (error) {
                    this.emailError =
                        error.response?.data?.errors?.email?.[0] ??
                        this.$t('common.generic_error');
                } finally {
                    this.processing = false;
                }
            },

            submit() {
                this.processing = true;

                axios.get('/sanctum/csrf-cookie').then(() => {
                    router.post(route('login'), this.form, {
                        onFinish: () => {
                            this.processing = false;
                        },
                    });
                });
            },

            goBack() {
                this.step = 1;
                this.form.password = '';
            },

            onStepEntered() {
                if (this.step === 2 && this.$refs.passwordInput) {
                    this.$refs.passwordInput.focus();
                }
            },
        },
        components: {
            ApplicationLogo,
            InputError,
            InputLabel,
            TextInput,
            Head,
            Link,
        },
    };
</script>